import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { BrandLogoLink } from './BrandLogoLink';

type AuthView = 'login' | 'signup' | 'forgot';

type AuthInlineWidgetProps = {
  /** Full-page, agent drawer, or home-page modal. */
  layout: 'page' | 'panel' | 'modal';
  /** Richer end-drawer look (larger type, feature chips) — `layout: 'panel'` only. */
  panelStyle?: 'default' | 'drawerPromo';
  defaultView?: AuthView;
  /** When sign-in and sign-up are shown as tabs, hide the inline switch links. */
  hideAuthSwitchLinks?: boolean;
  /** Hide title + subcopy (e.g. when a parent provides tabs). */
  hideHeader?: boolean;
  /** When sign-in succeeds (page mode: caller usually navigates). */
  onLoginSuccess?: () => void;
  /** When sign-up succeeds (page mode). */
  onSignupSuccess?: () => void;
  /** After login, used when `layout` is `page` and no `onLoginSuccess` is passed. */
  loginRedirectTo?: string;
  /** After signup, when `layout` is `page` and no `onSignupSuccess` is passed. */
  signupRedirectTo?: string;
};

export function AuthInlineWidget({
  layout,
  panelStyle = 'default',
  defaultView = 'login',
  hideAuthSwitchLinks = false,
  hideHeader = false,
  onLoginSuccess,
  onSignupSuccess,
  loginRedirectTo: loginRedirectToProp,
  signupRedirectTo: signupRedirectToProp,
}: AuthInlineWidgetProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as { from?: string } | null)?.from;
  const loginRedirectTo = loginRedirectToProp ?? fromState ?? '/library';
  const signupRedirectTo = signupRedirectToProp ?? '/library';

  const [view, setView] = useState<AuthView>(defaultView);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const isPage = layout === 'page';
  const isModal = layout === 'modal';
  const isDrawerPromo = layout === 'panel' && panelStyle === 'drawerPromo';

  const inputClass = isDrawerPromo
    ? 'mt-1.5 w-full rounded-xl border border-subtle/80 bg-surface/80 px-4 py-2.5 text-field shadow-sm transition focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25'
    : 'mt-1 w-full rounded-lg border border-subtle bg-base px-3 py-2 text-field focus:border-brand focus:outline-none';

  const afterLogin = () => {
    onLoginSuccess?.();
    if (layout === 'page' && !onLoginSuccess) {
      void navigate(loginRedirectTo, { replace: true });
    }
  };

  const afterSignup = () => {
    onSignupSuccess?.();
    if (layout === 'page' && !onSignupSuccess) {
      void navigate(signupRedirectTo, { replace: true });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: signErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signErr) {
        setError(signErr.message);
        return;
      }
      afterLogin();
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: signErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });
      if (signErr) {
        setError(signErr.message);
        return;
      }
      afterSignup();
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotDone(false);
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/update-password`;
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });
      if (resetErr) {
        setError(resetErr.message);
        return;
      }
      setForgotDone(true);
    } finally {
      setLoading(false);
    }
  };

  const title =
    view === 'forgot' ? 'Reset password' : view === 'login' ? 'Sign in' : 'Create account';

  const description = (() => {
    if (view === 'forgot') {
      return isDrawerPromo ? "We'll email a reset link." : "We'll email you a link to choose a new password.";
    }
    if (isDrawerPromo && (view === 'login' || view === 'signup')) {
      return '';
    }
    if (view === 'login') {
      return isPage
        ? 'Use your account to run apps and manage credits.'
        : isModal
          ? 'Sign in with your email and password.'
          : 'Sign in to set up your agent and chat in this panel.';
    }
    return isPage
      ? 'Start with credits and your personal app library.'
      : isModal
        ? 'Create an account to use the app store and your library.'
        : 'Create an account to use this agent in the panel.';
  })();

  const panelLikeCard = isDrawerPromo
    ? 'w-full space-y-5 rounded-3xl border border-subtle/60 bg-elevated/95 p-5 shadow-2xl shadow-black/15 ring-1 ring-inset ring-white/5 backdrop-blur-sm sm:space-y-6 sm:p-7'
    : 'w-full space-y-5 rounded-2xl border border-subtle bg-elevated/80 p-5 shadow-lg';

  const titleClass = isPage
    ? 'text-2xl font-semibold text-th-primary'
    : isDrawerPromo
      ? 'text-xl font-bold tracking-tight text-th-primary sm:text-2xl'
      : 'text-lg font-semibold text-th-primary';
  const descClass = isDrawerPromo
    ? 'mt-1.5 text-sm text-th-secondary'
    : 'mt-1.5 text-sm text-th-secondary';

  const primaryButtonClass = isDrawerPromo
    ? 'w-full rounded-xl bg-brand py-3 text-base font-semibold text-surface shadow-md shadow-black/20 transition hover:bg-brand-hover hover:shadow-lg active:scale-[0.99] disabled:opacity-50'
    : 'w-full rounded-lg bg-brand py-2.5 font-medium text-surface hover:bg-brand-hover disabled:opacity-50';

  const inner = (
    <div
      className={
        isPage
          ? 'w-full max-w-md space-y-8 rounded-2xl border border-subtle bg-elevated p-8 shadow-xl'
          : isModal
            ? `${panelLikeCard} sm:p-6`
            : panelLikeCard
      }
    >
      {!isPage && (
        <div className="flex justify-center">
          <BrandLogoLink
            className="shrink-0"
            imgClassName={
              isDrawerPromo
                ? 'h-10 w-auto max-w-[min(80vw,240px)] object-contain sm:h-12'
                : 'h-8 w-auto max-w-[min(80vw,220px)] object-contain'
            }
          />
        </div>
      )}
      {hideHeader && view !== 'forgot' ? null : (
        <div className={isDrawerPromo ? 'text-center' : undefined}>
          <h1 className={`${titleClass}${isDrawerPromo && !description ? ' mb-0' : ''}`}>{title}</h1>
          {isDrawerPromo && !description ? null : <p className={descClass}>{description}</p>}
        </div>
      )}

      {view === 'login' && (
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-th-primary">Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-th-primary">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </div>
          {error ? (
            <p className="text-sm text-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={loading} className={primaryButtonClass}>
            {loading ? 'Signing in…' : 'Continue'}
          </button>
          <p className="text-center text-sm">
            <button
              type="button"
              className="text-th-secondary hover:text-th-primary"
              onClick={() => {
                setError('');
                setView('forgot');
              }}
            >
              Forgot password?
            </button>
          </p>
        </form>
      )}

      {view === 'signup' && (
        <form onSubmit={handleSignup} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-th-primary">Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-th-primary">Password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </div>
          {error ? (
            <p className="text-sm text-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={loading} className={primaryButtonClass}>
            {loading ? 'Creating…' : 'Sign up'}
          </button>
        </form>
      )}

      {view === 'forgot' && (
        <form onSubmit={handleForgot} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-th-primary">Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
          {forgotDone ? (
            <p className="text-sm text-th-secondary" role="status">
              {isDrawerPromo
                ? 'If that address is on file, we sent a link.'
                : 'If an account exists for that address, we sent a reset link. You can set a new password on the next page.'}
            </p>
          ) : null}
          {error ? (
            <p className="text-sm text-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={loading} className={primaryButtonClass}>
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}

      {view === 'forgot' ? (
        <p className="text-center text-sm text-th-secondary">
          <button type="button" className="text-brand hover:underline" onClick={() => setView('login')}>
            Back to sign in
          </button>
        </p>
      ) : hideAuthSwitchLinks ? null : (
        <p className="text-center text-sm text-th-secondary">
          {view === 'login' ? (
            <>
              No account?{' '}
              {isPage ? (
                <Link to="/signup" className="text-brand hover:underline">
                  Create one
                </Link>
              ) : (
                <button type="button" className="text-brand hover:underline" onClick={() => setView('signup')}>
                  Create one
                </button>
              )}
            </>
          ) : (
            <>
              Already have an account?{' '}
              {isPage ? (
                <Link to="/login" className="text-brand hover:underline">
                  Sign in
                </Link>
              ) : (
                <button type="button" className="text-brand hover:underline" onClick={() => setView('login')}>
                  Sign in
                </button>
              )}
            </>
          )}
        </p>
      )}
      {isPage && view === 'login' ? (
        <p className="text-center text-xs text-th-tertiary">
          <Link to="/" className="hover:text-th-secondary">
            Back to home
          </Link>
        </p>
      ) : null}
    </div>
  );

  if (isPage) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-6">
        <BrandLogoLink />
        {inner}
      </div>
    );
  }

  return inner;
}
